// TutorSubmission.js

import React, { useState, useEffect, useContext } from "react";
import "../styles/TutorSubmission.css";
import Select from "react-select";
import { useNavigate, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Footer from "../Footer";
import { UserContext } from "../context/UserContext";

const Tutor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginUser } = useContext(UserContext);
  const [step, setStep] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [otpResent, setOtpResent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    profile_picture: "",
    institute_id: "",
    institute_custom: "",
    degree_id: "",
    degree_custom: "",
    field_of_study_id: "",
    field_custom: "",
    study_start_year: "",
    study_end_year: "",
    medium_id: "",
    level_id: "",
    subject_id: "",
    division_id: "",
    district_id: "",
    area_id: [],
    teaching_mode: "",
    availability: "",
    gender: "",
    bio: "",
    contact_phone: "",
    contact_whatsapp: "",
    email: "",
    password: "",
    expected_remuneration: "",
    account_type: "", // "Normal Tutor" | "Verified Tutor (Free Badge)"
    university_email: "",
    university_email_local: "",
    university_email_domain: "",

    otp: "",
    is_university_verified: false,
  });

  const goToStep = (nextStep) => {
    setErrorMessage(""); // always clear error first
    setStep(nextStep);
  };

  // Clear any lingering message whenever the step changes (belt & suspenders)
  useEffect(() => {
    setErrorMessage("");
  }, [step]);

  // ---- University email helpers ----
  const ALLOWED_UNIVERSITY_DOMAINS = [
    "aust.edu",
    "student.aiub.edu",
    "student.bau.edu.bd",
    "bubt.edu.bd",
    "buet.ac.bd",
    "bup.edu.bd",
    "stu.buft.edu.bd",
    "butex.edu.bd",
    "daffodilvarsity.edu.bd",
    "std.ewubd.edu",
    "iub.edu.bd",
    "iubat.edu",
    "iut-dhaka.edu",
    "juniv.edu",
    "stud.kuet.ac.bd",
    "mist.ac.bd",
    "northsouth.edu",
    "student.presidency.edu.bd",
    "primeasia.edu.bd",
    "ru.ac.bd",
    "student.ruet.ac.bd",
    "student.sust.edu",
    "seu.edu.bd",
    "stamforduniversity.edu.bd",
    "ulab.edu.bd",
    "du.ac.bd",
    "it.du.ac.bd",
    "std.cu.ac.bd",
    "uap-bd.edu",
    "g.bracu.ac.bd",
    "bracu.ac.bd",
    "student.cuet.ac.bd",
    "cityuniversity.edu.bd",
    "diu.edu",
  ];

  const normalizeEmail = (e = "") => {
    const t = e.trim().replace(/\s+/g, "");
    const parts = t.split("@");
    if (parts.length !== 2 || !parts[0] || !parts[1]) return "";
    return `${parts[0]}@${parts[1].toLowerCase()}`;
  };

  const isValidEmailSyntax = (email) =>
    /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/.test(
      email,
    );

  const domainHasEduOrAcLabel = (domain) => /\.(edu|ac)(\.|$)/i.test(domain);

  const domainIsExplicitlyAllowed = (domain) =>
    ALLOWED_UNIVERSITY_DOMAINS.includes(domain.toLowerCase());

  const isAllowedUniversityDomain = (domain) =>
    domainIsExplicitlyAllowed(domain) || domainHasEduOrAcLabel(domain);

  // Build final email from formData. Accepts full email in "local" field (contains '@')
  // OR username + domain/custom domain.
  // const buildUniversityEmail = (f) => {
  //   const rawLocal = String(f.university_email_local || "").trim();
  //   if (!rawLocal) return { email: "", isFullInput: false };

  //   // full email pasted
  //   if (rawLocal.includes("@")) {
  //     const email = normalizeEmail(rawLocal);
  //     return { email, isFullInput: true };
  //   }

  //   // two-piece (only for predefined domains)
  //   const domain = String(f.university_email_domain || "")
  //     .replace(/^@/, "")
  //     .toLowerCase();

  //   if (!domain) return { email: "", isFullInput: false };

  //   const email = `${rawLocal}@${domain}`;
  //   return { email, isFullInput: false };
  // };

  const buildUniversityEmail = (f) => {
    const raw = String(f.university_email_local || "").trim();
    if (!raw) return { email: "", isFullInput: true };

    const email = normalizeEmail(raw);
    return { email, isFullInput: true };
  };

  // Share Profile
  const extractTutorId = (data) =>
    data?.tutor_id ||
    data?.id ||
    data?.tutor?.id ||
    data?.tutorProfile?.id ||
    data?.user?.id ||
    data?.profile?.id ||
    null;

  const buildProfileUrl = (id) => {
    const origin =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "https://tn.goatteachers.com/";
    return `${origin}/tutor/${id}`;
  };

  const REDIRECT_STORAGE_KEY = "afterTutorRegisterRedirect";

  const getSafeRedirectPath = (value) => {
    if (!value || typeof value !== "string") return "";

    let path = value.trim();

    try {
      path = decodeURIComponent(path);
    } catch {
      // ignore decode error
    }

    // Only allow internal routes. This prevents unsafe external redirects.
    if (!path.startsWith("/") || path.startsWith("//")) return "";

    // Prevent redirect loops.
    const blockedRoutes = [
      "/tutor-submission",
      "/tutor-submission-regular",
      "/tutor-login",
      "/login",
      "/register",
    ];

    const isBlocked = blockedRoutes.some(
      (route) => path === route || path.startsWith(`${route}?`),
    );

    if (isBlocked) return "";

    return path;
  };

  const getIncomingRedirectPath = () => {
    const params = new URLSearchParams(location.search);

    return (
      getSafeRedirectPath(params.get("redirect")) ||
      getSafeRedirectPath(location.state?.from) ||
      ""
    );
  };

  const getAfterRegisterRedirectPath = () => {
    return (
      getIncomingRedirectPath() ||
      getSafeRedirectPath(sessionStorage.getItem(REDIRECT_STORAGE_KEY)) ||
      "/"
    );
  };

  // University verification UI state
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);

  const [options, setOptions] = useState({
    institutes: [],
    degrees: [],
    fields: [],
    mediums: [],
    levels: [],
    subjects: [],
    divisions: [],
    districts: [],
    areas: [],
  });

  const INVALID_UNI_EMAIL =
    "Invalid university email. Please enter the correct student email from your university portal.";

  const OTP_SENT_MESSAGE = "OTP sent! Check Inbox, Spam, Junk, or Promotions.";

  const showInvalidEmailToast = () => {
    toast.error(INVALID_UNI_EMAIL, {
      toastId: "invalid-uni-email",
    });
  };

  const questions = [
    { label: "Your full name?", key: "name" },
    {
      label: "Your education institute?",
      key: "institute_id",
      list: "institutes",
      custom: true,
    },
    {
      label: "Running / Highest Qualification?",
      key: "degree_id",
      list: "degrees",
      custom: true,
    },
    {
      label: "Your field of study?",
      key: "field_of_study_id",
      list: "fields",
      custom: true,
    },

    {
      label: "Medium(s) you want to teach?",
      key: "medium_id",
      list: "mediums",
      multi: true,
    },
    {
      label: "Level(s) of education you want to teach?",
      key: "level_id",
      list: "levels",
      multi: true,
    },
    {
      label: "Subject(s) you want to teach?",
      key: "subject_id",
      list: "subjects",
      multi: true,
    },

    {
      label: "Preferred teaching mode?",
      key: "teaching_mode",
      select: ["Online", "In-person", "Both"],
    },
    {
      label: "Your weekly availability",
      key: "availability",
      multiSelect: [
        "Saturday",
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
      ],
    },
    {
      label: "Your Location",
      key: "location",
      group: true,
      multiArea: true,
    },

    {
      label: "Gender?",
      key: "gender",
      select: ["Male", "Female"],
    },
    { label: "Write a short bio", key: "bio" },
    {
      label: "Upload your profile picture",
      key: "profile_picture",
      type: "file",
    },
    { label: "Your phone number", key: "contact_phone" },
    {
      label: "Your WhatsApp contact",
      key: "contact_whatsapp",
      button: true,
    },
    {
      label: "How do you want to create your tutor profile?",
      key: "account_type",
    },
    {
      label: formData.account_type?.startsWith("Verified")
        ? "Your university student email address"
        : "Your email address",
      key: "email",
    },
    { label: "Choose a password", key: "password", type: "password" },
  ];

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const urls = [
          ["institutes", "https://tn.server.goatteachers.com/api/institutes"],
          ["degrees", "https://tn.server.goatteachers.com/api/degrees"],
          ["fields", "https://tn.server.goatteachers.com/api/fields"],
          ["mediums", "https://tn.server.goatteachers.com/api/mediums"],
          ["levels", "https://tn.server.goatteachers.com/api/levels"],
          ["subjects", "https://tn.server.goatteachers.com/api/subjects"],
          ["divisions", "https://tn.server.goatteachers.com/api/divisions"],
          ["districts", "https://tn.server.goatteachers.com/api/districts"],
          ["areas", "https://tn.server.goatteachers.com/api/areas"],
        ];

        const responses = await Promise.all(
          urls.map(([key, url]) =>
            fetch(url).then(async (res) => {
              if (!res.ok) throw new Error(`${key} → ${res.status}`);
              return res.json();
            }),
          ),
        );

        setOptions({
          institutes: responses[0],
          degrees: responses[1],
          fields: responses[2],
          mediums: responses[3],
          levels: responses[4],
          subjects: responses[5],
          divisions: responses[6],
          districts: responses[7],
          areas: responses[8],
        });
      } catch (err) {
        console.error("❌ Failed to fetch dropdown options:", err.message);
      }
    };

    fetchOptions();
  }, []);

  useEffect(() => {
    const incomingRedirect = getIncomingRedirectPath();

    if (incomingRedirect) {
      sessionStorage.setItem(REDIRECT_STORAGE_KEY, incomingRedirect);
    }
  }, [location.search, location.state]);

  const handleChange = (e) => {
    const { type, name, value, files } = e.target;

    let newValue = type === "file" ? files[0] : value;

    // Capitalize each word's first letter for tutor name
    if (name === "name") {
      newValue = value.replace(/\b\p{L}/gu, (m) => m.toUpperCase());
    }

    // Capitalize only the very first letter of the bio
    if (name === "bio") {
      newValue = value.replace(/^(\s*\p{L})/u, (m) => m.toUpperCase());
    }

    setFormData({
      ...formData,
      [name]: newValue,
    });
    setErrorMessage(""); // clear error on any native input change
  };

  const duplicatePhoneToWhatsApp = () => {
    setFormData((prev) => ({
      ...prev,
      contact_whatsapp: prev.contact_phone,
    }));
    setErrorMessage(""); // clear if user fixes via copy
  };

  // handle submit

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Build the email we’ll use for fallback login (don’t rely on setState timing)
    let effectiveEmail = "";

    if (!formData.account_type) {
      setErrorMessage("Please choose Verified Tutor or Normal Tutor.");
      return;
    }

    // Block submit if email not valid per path
    if (formData.account_type?.startsWith("Verified")) {
      const { email } = buildUniversityEmail(formData);
      const domain = (email.split("@")[1] || "").toLowerCase();

      if (!otpVerified || !formData.is_university_verified) {
        setErrorMessage("Please verify your university email with OTP first.");
        return;
      }

      if (
        !email ||
        !isValidEmailSyntax(email) ||
        !isAllowedUniversityDomain(domain)
      ) {
        setErrorMessage(
          "Email must be from allowed list or any '.edu' domain.",
        );
        return;
      }

      effectiveEmail = email;
    } else {
      const normalEmail = normalizeEmail(formData.email || "");

      if (!isValidEmailSyntax(normalEmail)) {
        setErrorMessage("Enter a valid email address.");
        return;
      }

      effectiveEmail = normalEmail;
    }

    const formDataToSend = new FormData();

    // Profile picture
    if (formData.profile_picture instanceof File) {
      formDataToSend.append("profile_picture", formData.profile_picture);
    }

    // Profile subjects
    const profile_subjects = [];
    (formData.subject_id || []).forEach((subjectId) => {
      (formData.level_id || []).forEach((levelId) => {
        (formData.medium_id || []).forEach((mediumId) => {
          profile_subjects.push({
            subject_id: parseInt(subjectId),
            level_id: parseInt(levelId),
            medium_id: parseInt(mediumId),
          });
        });
      });
    });
    formDataToSend.append("profile_subjects", JSON.stringify(profile_subjects));

    //  Always send an array of integers
    const areasArray = Array.isArray(formData.area_id)
      ? formData.area_id
      : formData.area_id
        ? [formData.area_id]
        : [];

    formDataToSend.append(
      "areas",
      JSON.stringify(areasArray.map((id) => parseInt(id))),
    );

    // Availability
    const availabilityArray = Array.isArray(formData.availability)
      ? formData.availability
      : typeof formData.availability === "string"
        ? formData.availability.split(",").map((day) => day.trim())
        : [];
    formDataToSend.append("availability", JSON.stringify(availabilityArray));

    // Password
    formDataToSend.append("password_hash", formData.password);

    // Study years
    const safeYear = (val) =>
      val === "" || val === undefined || val === null ? null : parseInt(val);
    formDataToSend.append(
      "study_start_year",
      safeYear(formData.study_start_year),
    );
    formDataToSend.append("study_end_year", safeYear(formData.study_end_year));

    // Other fields
    const exclude = [
      "password",
      "profile_picture",
      "subject_id",
      "level_id",
      "medium_id",
      "availability",
      "study_start_year",
      "study_end_year",
      "area_id",
    ];

    for (const key in formData) {
      if (!exclude.includes(key) && formData[key] !== undefined) {
        formDataToSend.append(key, formData[key]);
      }
    }

    if (formData.account_type === "Normal Tutor") {
      formDataToSend.set("email", effectiveEmail);
      formDataToSend.set("account_type", "Normal Tutor");
      formDataToSend.set("university_email", "");
      formDataToSend.set("university_email_local", "");
      formDataToSend.set("university_email_domain", "");
      formDataToSend.set("otp", "");
      formDataToSend.set("is_university_verified", "false");
    }

    if (formData.account_type?.startsWith("Verified")) {
      formDataToSend.set("email", effectiveEmail);
      formDataToSend.set("account_type", "Verified Tutor (Free Badge)");
      formDataToSend.set("university_email", effectiveEmail);
      formDataToSend.set("is_university_verified", "true");
    }

    try {
      const res = await fetch(
        "https://tn.server.goatteachers.com/api/tutors/register",
        {
          method: "POST",
          body: formDataToSend,
          credentials: "include",
        },
      );

      const data = await res.json();

      if (res.ok) {
        try {
          const loginRes = await fetch(
            "https://tn.server.goatteachers.com/api/tutors/login",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                email: effectiveEmail,
                password: formData.password,
              }),
            },
          );

          const loginData = await loginRes.json();

          if (!loginRes.ok || !loginData?.token) {
            setSuccessMessage("");
            setErrorMessage(
              "Registration successful, but auto-login failed. Please login manually.",
            );
            return;
          }

          const loggedInTutor = {
            ...(loginData.tutor || {}),
            email: loginData.tutor?.email || effectiveEmail,
          };

          // This updates React UserContext immediately
          loginUser(loggedInTutor, loginData.token, "tutor");

          // Keep these for old parts of your app that may still check them
          localStorage.setItem("authToken", loginData.token);
          localStorage.setItem("token", loginData.token);

          localStorage.setItem(
            "tutor",
            JSON.stringify({
              ...loggedInTutor,
              role: "tutor",
              token: loginData.token,
            }),
          );

          window.dispatchEvent(new Event("authChanged"));
          window.dispatchEvent(new Event("tutorLoggedIn"));

          const redirectPath = getAfterRegisterRedirectPath();

          sessionStorage.removeItem(REDIRECT_STORAGE_KEY);

          setErrorMessage("");
          setSuccessMessage("Registration successful! You are now logged in.");

          navigate(redirectPath, { replace: true });

          return;
        } catch (loginError) {
          console.warn("Auto-login failed:", loginError);
          setSuccessMessage("");
          setErrorMessage(
            "Registration successful, but auto-login failed. Please login manually.",
          );
          return;
        }
      } else {
        if (data?.code === "FILE_TOO_LARGE") {
          setErrorMessage(
            "Profile picture is too large. Please upload an image under 5 MB.",
          );
        } else {
          setErrorMessage(`❌ ${data.message || "Registration failed."}`);
        }
      }
    } catch (err) {
      console.error("❌ Submission error:", err);
      setErrorMessage("❌ Network or server error.");
    }
  };

  const renderInput = (q) => {
    const handleSelectChange = (selectedOption, fieldName) => {
      setFormData((prev) => ({
        ...prev,
        [fieldName]: selectedOption ? selectedOption.value : "",
      }));
      setErrorMessage(""); // clear for single selects
    };

    // ----- LOCATION GROUP -----
    if (q.group && q.key === "location") {
      return (
        <>
          <Select
            className="custom-select"
            placeholder="Select Division"
            value={
              options.divisions
                .map((d) => ({ value: d.id, label: d.name }))
                .find((opt) => opt.value === formData.division_id) || null
            }
            onChange={(selected) =>
              setFormData((prev) => ({
                ...prev,
                division_id: selected ? selected.value : "",
                district_id: "",
                area_id: [], // reset on division change
              }))
            }
            onMenuOpen={() => setErrorMessage("")}
            onFocus={() => setErrorMessage("")}
            options={options.divisions.map((d) => ({
              value: d.id,
              label: d.name,
            }))}
          />

          <Select
            className="custom-select"
            placeholder="Select District"
            value={
              options.districts
                .map((d) => ({ value: d.id, label: d.name }))
                .find((opt) => opt.value === formData.district_id) || null
            }
            onChange={(selected) =>
              setFormData((prev) => ({
                ...prev,
                district_id: selected ? selected.value : "",
                area_id: [], // reset on district change
              }))
            }
            onMenuOpen={() => setErrorMessage("")}
            onFocus={() => setErrorMessage("")}
            options={options.districts
              .filter(
                (d) =>
                  !formData.division_id ||
                  d.division_id === formData.division_id,
              )
              .map((d) => ({ value: d.id, label: d.name }))}
          />

          {/* MULTI AREA */}
          <Select
            isMulti
            className="custom-select"
            placeholder="You Can Select Multiple Areas"
            value={(formData.area_id || [])
              .map((aId) => {
                const areaObj = options.areas.find((a) => a.id === aId);
                return areaObj
                  ? { value: areaObj.id, label: aObjName(areaObj) }
                  : null;
              })
              .filter(Boolean)}
            onChange={(selected) => {
              setFormData((prev) => ({
                ...prev,
                area_id: (selected || []).map((s) => parseInt(s.value)),
              }));
              setErrorMessage(""); // clear when multi area changes
            }}
            onMenuOpen={() => setErrorMessage("")}
            onFocus={() => setErrorMessage("")}
            options={options.areas
              .filter(
                (a) =>
                  !formData.district_id ||
                  a.district_id === formData.district_id,
              )
              .map((a) => ({ value: a.id, label: a.name }))}
          />
        </>
      );
    }

    // helper to avoid repeating a?.name more than once
    function aObjName(a) {
      return a.name;
    }

    // ----- GENERIC MULTI-SELECT (string arrays) -----
    if (q.multiSelect) {
      return (
        <Select
          isMulti
          className="custom-select"
          placeholder={`Select ${q.label}`}
          value={(formData[q.key] || []).map((val) => ({
            value: val,
            label: val,
          }))}
          onChange={(selected) => {
            setFormData((prev) => ({
              ...prev,
              [q.key]: (selected || []).map((s) => s.value),
            }));
            setErrorMessage(""); // clear when multi select changes
          }}
          onMenuOpen={() => setErrorMessage("")}
          onFocus={() => setErrorMessage("")}
          options={q.multiSelect.map((opt) => ({ value: opt, label: opt }))}
        />
      );
    }

    // ----- SIMPLE SELECT (fixed options) -----
    if (q.select) {
      return (
        <Select
          className="custom-select"
          placeholder={`Select ${q.label}`}
          value={
            q.select
              .map((opt) => ({ value: opt, label: opt }))
              .find((o) => o.value === formData[q.key]) || null
          }
          onChange={(selected) => handleSelectChange(selected, q.key)}
          onMenuOpen={() => setErrorMessage("")}
          onFocus={() => setErrorMessage("")}
          options={q.select.map((opt) => ({ value: opt, label: opt }))}
        />
      );
    }

    // ----- LIST-DRIVEN SELECT (fetched options) -----
    if (q.list) {
      const listOptions = (options[q.list] || []).map((opt) => ({
        value: opt.id,
        label: opt.name,
      }));

      return (
        <>
          <Select
            isMulti={!!q.multi}
            className="custom-select"
            placeholder={`Select ${q.label}`}
            value={
              q.multi
                ? (formData[q.key] || []).map((id) => ({
                    value: id,
                    label:
                      (options[q.list] || []).find((opt) => opt.id === id)
                        ?.name || "",
                  }))
                : listOptions.find((o) => o.value === formData[q.key]) || null
            }
            onChange={(selected) => {
              setFormData((prev) => ({
                ...prev,
                [q.key]: q.multi
                  ? (selected || []).map((s) => s.value)
                  : selected?.value || "",
              }));
              setErrorMessage(""); // clear when list-driven select changes
            }}
            onMenuOpen={() => setErrorMessage("")}
            onFocus={() => setErrorMessage("")}
            options={listOptions}
          />

          {/* Alternative Data Input */}

          {/* {q.custom && (
            <input
              type="text"
              name={`${q.key.split("_")[0]}_custom`}
              placeholder={`Or type your ${q.label.toLowerCase()}`}
              onChange={handleChange}
              value={formData[`${q.key.split("_")[0]}_custom`] || ""}
            />
          )} */}
        </>
      );
    }

    // ----- ACCOUNT TYPE (Normal vs Verified) -----
    if (q.key === "account_type") {
      return (
        <div className="account-type-box">
          <div className="account-type-options">
            <div
              className={`option-card ${
                formData.account_type === "Verified Tutor (Free Badge)"
                  ? "selected"
                  : ""
              }`}
              onClick={() => {
                setFormData((prev) => ({
                  ...prev,
                  account_type: "Verified Tutor (Free Badge)",
                  email: "",
                  university_email: "",
                  university_email_local: "",
                  university_email_domain: "",
                  otp: "",
                  is_university_verified: false,
                }));

                setOtpSent(false);
                setOtpVerified(false);
                setOtpResent(false);
                setErrorMessage("");
              }}
              onMouseDown={() => setErrorMessage("")}
            >
              <h4>Verified Tutor</h4>
              <p>
                Verify with your <strong>university email (.edu / .ac)</strong>{" "}
                to get a <span className="highlight">FREE verified badge</span>{" "}
                and higher reach.
              </p>
            </div>

            <div
              className={`option-card ${
                formData.account_type === "Normal Tutor" ? "selected" : ""
              }`}
              onClick={() => {
                setFormData((prev) => ({
                  ...prev,
                  account_type: "Normal Tutor",
                  email: "",
                  university_email: "",
                  university_email_local: "",
                  university_email_domain: "",
                  otp: "",
                  is_university_verified: false,
                }));

                setOtpSent(false);
                setOtpVerified(false);
                setOtpResent(false);
                setErrorMessage("");
              }}
              onMouseDown={() => setErrorMessage("")}
            >
              <h4>Normal Tutor</h4>
              <p>
                Register with any valid email address. No university email or
                OTP verification needed.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // ----- EMAIL (Verified-only, full email input) -----
    if (q.key === "email") {
      const isVerifiedPath = formData.account_type?.startsWith("Verified");

      if (!isVerifiedPath) {
        return (
          <div className="university-email-box">
            <p
              className="simple-hint"
              style={{
                margin: "8px 0 10px",
                fontSize: "0.95rem",
                lineHeight: 1.35,
              }}
            >
              Enter your regular email address.
            </p>

            <input
              type="email"
              name="email"
              placeholder="example@gmail.com"
              value={formData.email || ""}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  email: e.target.value,
                  university_email: "",
                  university_email_local: "",
                  university_email_domain: "",
                  otp: "",
                  is_university_verified: false,
                }));

                setErrorMessage("");
              }}
              autoComplete="email"
            />
          </div>
        );
      }

      const { email } = buildUniversityEmail(formData);
      const domain = (email.split("@")[1] || "").toLowerCase();

      const emailLooksOk = email && isValidEmailSyntax(email);
      const emailAllowed = emailLooksOk && isAllowedUniversityDomain(domain);

      return (
        <div className="university-email-box">
          {!otpVerified ? (
            <>
              <p
                className="simple-hint"
                style={{
                  margin: "8px 0 10px",
                  fontSize: "0.95rem",
                  lineHeight: 1.35,
                }}
              >
                <div className="email-instructions">
                  <strong>Use your university student email</strong>

                  <p>
                    Enter the email given by your university, exactly as shown
                    in your student portal.
                  </p>

                  <div className="email-examples">
                    <span>Examples:</span>
                    <code>username@du.ac.bd</code>
                    <code>username@student.bracu.ac.bd</code>
                    <code>username@northsouth.edu.bd</code>
                  </div>

                  <p className="email-warning">
                    ⚠️ Do not use Gmail/Yahoo. Wrong email means you will not
                    receive the OTP.
                  </p>
                </div>
              </p>

              <input
                type="email"
                name="university_email_local"
                placeholder="@university.edu / @university.ac.bd"
                value={formData.university_email_local || ""}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  setFormData((p) => ({
                    ...p,
                    university_email_local: v,
                    university_email: normalizeEmail(v),
                  }));
                  setErrorMessage("");
                }}
                autoComplete="email"
              />

              <div className="email-preview">
                <small>
                  Will verify: <code>{email || "—"}</code>
                </small>
                {!!email && (!emailLooksOk || !emailAllowed) && (
                  <div className="form-error-message" style={{ marginTop: 6 }}>
                    This email is invalid or doesn't exist. Please try again
                    with the correct one or recheck the email.
                  </div>
                )}
              </div>

              {!otpSent ? (
                <button
                  type="button"
                  className={`verify-btn ${isSendingOtp ? "verify-btn-loading" : ""} ${
                    otpSent ? "verify-btn-success" : ""
                  }`}
                  disabled={isSendingOtp || otpSent}
                  onClick={async () => {
                    if (isSendingOtp || otpSent) return;

                    if (!emailLooksOk) {
                      setErrorMessage("");
                      showInvalidEmailToast();
                      return;
                    }

                    if (!emailAllowed) {
                      setErrorMessage("");
                      showInvalidEmailToast();
                      return;
                    }

                    try {
                      setIsSendingOtp(true);

                      const res = await fetch(
                        "https://tn.server.goatteachers.com/api/tutors/otp/send",
                        {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ email }),
                        },
                      );

                      const data = await res.json();

                      if (res.ok) {
                        setOtpSent(true);
                        setFormData((p) => ({
                          ...p,
                          university_email: email,
                        }));
                        setErrorMessage("");
                        toast.success(OTP_SENT_MESSAGE, {
                          toastId: "otp-sent",
                        });
                      } else {
                        setErrorMessage("");
                        showInvalidEmailToast();
                      }
                    } catch (err) {
                      setErrorMessage("");
                      showInvalidEmailToast();
                    } finally {
                      setIsSendingOtp(false);
                    }
                  }}
                >
                  {isSendingOtp
                    ? "Sending OTP..."
                    : otpSent
                      ? "OTP Sent"
                      : "Send Verification OTP"}
                </button>
              ) : (
                <>
                  <input
                    type="text"
                    name="otp"
                    placeholder="Enter OTP"
                    value={formData.otp || ""}
                    onChange={(e) => {
                      setFormData((p) => ({ ...p, otp: e.target.value }));
                      setErrorMessage("");
                    }}
                  />

                  <div
                    style={{ display: "flex", gap: "10px", marginTop: "8px" }}
                  >
                    <button
                      type="button"
                      className={`verify-btn ${isVerifyingOtp ? "verify-btn-loading" : ""} ${
                        otpVerified ? "verify-btn-success" : ""
                      }`}
                      disabled={isVerifyingOtp || isResendingOtp || otpVerified}
                      onClick={async () => {
                        if (isVerifyingOtp || isResendingOtp || otpVerified)
                          return;

                        const finalEmail = normalizeEmail(
                          formData.university_email || email || "",
                        );
                        const otp = (formData.otp || "").trim();

                        if (!finalEmail || !otp) {
                          setErrorMessage("");
                          toast.warning("Please enter the OTP first.", {
                            toastId: "otp-required",
                          });
                          return;
                        }

                        try {
                          setIsVerifyingOtp(true);

                          const res = await fetch(
                            "https://tn.server.goatteachers.com/api/tutors/otp/verify",
                            {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ email: finalEmail, otp }),
                            },
                          );

                          if (res.ok) {
                            setOtpVerified(true);
                            setFormData((p) => ({
                              ...p,
                              is_university_verified: true,
                              university_email: finalEmail,
                              email: finalEmail,
                            }));
                            setErrorMessage("");
                            toast.success(
                              "University email verified successfully!",
                              {
                                toastId: "otp-verified",
                              },
                            );
                          } else {
                            setErrorMessage("");
                            toast.error(
                              "Invalid OTP. Please check the code and try again.",
                              {
                                toastId: "invalid-otp",
                              },
                            );
                          }
                        } catch {
                          setErrorMessage("");
                          toast.error(
                            "Invalid OTP. Please check the code and try again.",
                            {
                              toastId: "invalid-otp",
                            },
                          );
                        } finally {
                          setIsVerifyingOtp(false);
                        }
                      }}
                    >
                      {isVerifyingOtp
                        ? "Verifying OTP..."
                        : otpVerified
                          ? "Verified"
                          : "Verify OTP"}
                    </button>

                    <button
                      type="button"
                      className={`verify-btn secondary ${
                        isResendingOtp ? "verify-btn-loading" : ""
                      } ${otpResent ? "verify-btn-success" : ""}`}
                      disabled={
                        isSendingOtp ||
                        isVerifyingOtp ||
                        isResendingOtp ||
                        otpVerified
                      }
                      onClick={async () => {
                        if (
                          isSendingOtp ||
                          isVerifyingOtp ||
                          isResendingOtp ||
                          otpVerified
                        )
                          return;

                        const resendEmail = normalizeEmail(
                          formData.university_email || email || "",
                        );

                        if (!resendEmail) {
                          setErrorMessage("");
                          showInvalidEmailToast();
                          return;
                        }

                        try {
                          setIsResendingOtp(true);
                          setOtpResent(false);

                          const res = await fetch(
                            "https://tn.server.goatteachers.com/api/tutors/otp/send",
                            {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ email: resendEmail }),
                            },
                          );

                          if (res.ok) {
                            setOtpResent(true);
                            setErrorMessage("");
                            toast.info(
                              "OTP resent. Please check Inbox, Spam, or Junk.",
                              {
                                toastId: "otp-resent",
                              },
                            );

                            setTimeout(() => {
                              setOtpResent(false);
                            }, 2500);
                          } else {
                            setErrorMessage("");
                            showInvalidEmailToast();
                          }
                        } catch {
                          setErrorMessage("");
                          showInvalidEmailToast();
                        } finally {
                          setIsResendingOtp(false);
                        }
                      }}
                    >
                      {isResendingOtp
                        ? "Resending OTP..."
                        : otpResent
                          ? "OTP Resent"
                          : "Resend OTP"}
                    </button>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <p className="success-text">
                University email verified! Your account email is set to:
              </p>
              <input
                type="text"
                name="email"
                value={formData.email || ""}
                readOnly
              />
            </>
          )}
        </div>
      );
    }

    // ----- PASSWORD (disabled until verified on verified path) + SHOW/HIDE -----
    if (q.key === "password") {
      const isVerifiedPath = formData.account_type?.startsWith("Verified");
      const disabled = isVerifiedPath && !otpVerified;

      return (
        <>
          {isVerifiedPath && !otpVerified && (
            <p className="warn-text">
              Please complete university email verification first to continue as
              a Verified Tutor.
            </p>
          )}

          <div className="password-field-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              name={q.key}
              value={formData[q.key] || ""}
              onChange={handleChange}
              placeholder="Choose a password"
              disabled={disabled}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="toggle-password-btn"
              onClick={() => {
                setShowPassword((v) => !v);
                setErrorMessage("");
              }}
              aria-label={showPassword ? "Hide password" : "Show password"}
              disabled={disabled} // keep consistent UX: if input is disabled, so is toggle
            >
              {showPassword ? "🙈" : "👀"}
            </button>
          </div>
        </>
      );
    }

    // ----- PHONE NUMBER VALIDATION -----
    if (q.key === "contact_phone") {
      return (
        <input
          type="text"
          name="contact_phone"
          value={formData.contact_phone || ""}
          inputMode="numeric"
          pattern="\d*"
          maxLength={11} // hard cap at 11 chars
          onChange={(e) => {
            // allow digits only, cap at 11
            const digits = e.target.value.replace(/\D/g, "").slice(0, 11);
            setFormData({ ...formData, contact_phone: digits });
            setErrorMessage("");
          }}
          placeholder="Enter your 11-digit phone number"
          autoComplete="tel"
        />
      );
    }

    // ----- WHATSAPP COPY BUTTON -----
    if (q.key === "contact_whatsapp" && q.button) {
      return (
        <>
          <input
            type="text"
            name="contact_whatsapp"
            value={formData.contact_whatsapp || ""}
            onChange={handleChange}
            placeholder="WhatsApp Number"
          />
          <button
            type="button"
            className="copy-button"
            onClick={duplicatePhoneToWhatsApp}
          >
            Click me if same as phone number
          </button>
        </>
      );
    }

    // ----- FILE INPUT -----
    if (q.type === "file") {
      return (
        <input
          type="file"
          accept="image/*,.heic,.heif"
          name={q.key}
          onChange={handleChange}
        />
      );
    }

    // ----- DEFAULT INPUT -----
    return (
      <input
        type={q.type || "text"}
        name={q.key}
        value={formData[q.key] || ""}
        onChange={handleChange}
        placeholder="Type your answer here..."
      />
    );
  };

  const current = questions[step];
  const isLast = step === questions.length - 1;
  const isFirst = step === 0;

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3500}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        theme="colored"
      />

      <div className="form-container">
        {step === 0 && (
          <div className="mx-auto max-w-xl p-6 shadow-sm">
            {/* <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-white baloo-da-2-tutor">
              <span className="text-xl">🎓</span> Create Your Tutor Profile
            </h3>

            <p className="text-sm leading-relaxed text-white baloo-da-2-tutor">
              You can create a{" "}
              <span className="font-semibold">Verified Tutor</span> profile
              using your university email, or create a{" "}
              <span className="font-semibold">Normal Tutor</span> profile with
              any valid email address.
            </p>

            <p className="mt-3 text-sm text-green">
              Verified Tutor profiles receive a free badge after university
              email OTP verification.
            </p> */}
          </div>
        )}

        {/* <form
          onSubmit={
            isLast
              ? handleSubmit
              : (e) => {
                  e.preventDefault();
                  setStep(step + 1);
                }
          }
        > */}
        <form
          onSubmit={(e) => {
            e.preventDefault();

            const q = questions[step];
            const key = q.key;

            // 🔒 1) SPECIAL CASE: LOCATION GROUP
            if (key === "location") {
              if (!formData.division_id) {
                setErrorMessage("Please select a Division.");
                return;
              }
              if (!formData.district_id) {
                setErrorMessage("Please select a District.");
                return;
              }
              if (
                !Array.isArray(formData.area_id) ||
                formData.area_id.length === 0
              ) {
                setErrorMessage("Please select at least one Area.");
                return;
              }
              // ok → move on
              if (isLast) {
                setErrorMessage(""); // clear error before submit
                handleSubmit(e);
              } else {
                goToStep(step + 1); // use helper
              }

              return;
            }

            // 🔒 2) SPECIAL CASE: FILE INPUT REQUIRED
            if (
              q.type === "file" &&
              !(formData.profile_picture instanceof File)
            ) {
              setErrorMessage("⚠️ Please upload your profile picture.");
              return;
            }

            // 🔒 3) SPECIAL CASE: CUSTOM-OR-SELECT FIELDS (either is fine)
            const customFieldMap = {
              institute_id: "institute_custom",
              degree_id: "degree_custom",
              field_of_study_id: "field_custom",
            };
            if (customFieldMap[key]) {
              const customKey = customFieldMap[key];
              const hasSelect = !!formData[key];
              const hasCustom =
                typeof formData[customKey] === "string" &&
                formData[customKey].trim().length > 0;
              if (!hasSelect && !hasCustom) {
                setErrorMessage(
                  "Please select from the list or type your answer.",
                );
                return;
              }
            } else {
              // 🔒 4) GENERIC REQUIRED CHECK
              const val = formData[key];
              if (
                val === "" ||
                val === undefined ||
                (Array.isArray(val) && val.length === 0)
              ) {
                setErrorMessage(
                  "⚠️ Please fill out this field before continuing.",
                );
                return;
              }
            }

            // 🔒 5) PHONE: exactly 11 digits
            if (key === "contact_phone") {
              const digits = String(formData.contact_phone || "").replace(
                /\D/g,
                "",
              );
              if (digits.length !== 11) {
                setErrorMessage("❌ Phone number must be at least 11 digits.");
                return;
              }
            }

            //  advance or submit
            if (isLast) {
              setErrorMessage(""); // clear error before final submit
              handleSubmit(e);
            } else {
              goToStep(step + 1); // always clear then advance
            }
          }}
        >
          <p className="step-indicator">
            {step < 10
              ? `${step + 1} → Let's start with the basics`
              : `${step + 1} → Continue filling out your info`}
          </p>
          {errorMessage && (
            <div className="form-error-message">{errorMessage}</div>
          )}

          {successMessage && (
            <div className="form-success-message">{successMessage}</div>
          )}

          <label className="question-label">{current.label}</label>
          <div className="input-field">{renderInput(current)}</div>
          <div className="form-nav-buttons">
            {!isFirst && (
              <button
                type="button"
                className="nav-button back"
                onClick={() => goToStep(step - 1)}
              >
                ← Back
              </button>
            )}
            <button className="ok-button" type="submit">
              {isLast ? "Apply & Register" : "Next →"}
            </button>
          </div>
        </form>
      </div>

      <Footer />
    </>
  );
};

export default Tutor;
